import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutTemplate, PenSquare, Trash2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { assetUrl, errMsg, formatDate, useApi } from "@/lib/api";

function TemplateCard({ t, onUse, onDelete, index }) {
  return (
    <article className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-sky-300 transition-[border-color] fade-up" style={{ animationDelay: `${index * 40}ms` }} data-testid={`template-card-${t.id}`}>
      <div className="aspect-video bg-slate-100 overflow-hidden">
        {t.thumbnail_url ? <img src={assetUrl(t.thumbnail_url)} alt="" className="h-full w-full object-cover object-top" />
          : <div className="h-full w-full flex items-end p-5" style={{ background: `linear-gradient(135deg, ${t.primary_color || "#334155"} 0%, #0F172A 130%)` }}><LayoutTemplate className="text-white/80" /></div>}
      </div>
      <div className="p-5">
        <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 truncate">{t.category || "Template"} · {t.pages_count} pagine</p>
        <h3 className="mt-1 font-semibold text-slate-900 truncate" data-testid="template-name">{t.name}</h3>
        {t.description && <p className="mt-1 text-xs text-slate-600 line-clamp-2">{t.description}</p>}
        <p className="mt-2 text-[11px] text-slate-500">Da «{t.source_name || t.company_name}» · {formatDate(t.created_at)}</p>
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
          <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => onUse(t)} data-testid={`template-use-btn-${t.id}`}><Wand2 size={14} className="mr-1.5" />Nuovo sito da template</Button>
          <div className="flex">
            <Button asChild variant="ghost" size="icon" className="h-8 w-8 text-slate-500" aria-label="Modifica template"><Link to={`/projects/${t.id}`} data-testid={`template-edit-btn-${t.id}`}><PenSquare size={16} /></Link></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => onDelete(t)} aria-label="Elimina template" data-testid={`template-delete-btn-${t.id}`}><Trash2 size={16} /></Button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function TemplatesGrid({ onUse, onCount }) {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const load = useCallback(() => api.get("/templates").then(({ data }) => { setItems(data.projects); onCount?.(data.projects.length); }).catch((e) => { toast.error(errMsg(e)); setItems([]); }), [api, onCount]);
  useEffect(() => { load(); }, [load]);
  const remove = async () => {
    const t = deleting; setDeleting(null);
    try { await api.delete(`/projects/${t.id}`); toast.success(`Template «${t.name}» spostato nel cestino`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="templates-grid">
        {items === null && [0, 1, 2].map((i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
        {items?.map((t, i) => <TemplateCard key={t.id} t={t} index={i} onUse={onUse} onDelete={setDeleting} />)}
      </div>
      {items?.length === 0 && (
        <div className="mt-6 border border-dashed border-slate-300 rounded-xl p-12 text-center bg-white" data-testid="empty-templates">
          <LayoutTemplate className="mx-auto text-slate-400" />
          <p className="mt-3 font-medium text-slate-800">Nessun template ancora</p>
          <p className="mt-1 text-sm text-slate-500">Apri un progetto riuscito e usa «Salva come template» per riutilizzarlo con nuovi clienti.</p>
        </div>
      )}
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} destructive confirmLabel="Sposta nel cestino"
        title={`Eliminare il template «${deleting?.name}»?`} description="I siti già creati da questo template non vengono toccati. Potrai ripristinarlo dal cestino per 30 giorni." onConfirm={remove} />
    </>
  );
}
